import type { ReactNode } from 'react';

const SIZE = 160;
const THICKNESS = 24;
const R = (SIZE - THICKNESS) / 2;
const C = SIZE / 2;

/** Tones by position, cycling past the fourth slice. Danger slices skip the count. */
const TONES = [
  'donut-chart-tone-1',
  'donut-chart-tone-2',
  'donut-chart-tone-3',
  'donut-chart-tone-4',
];

/** The point on the ring at `turn` (0–1), clockwise from 12 o'clock. */
function at(turn: number): string {
  const a = turn * 2 * Math.PI - Math.PI / 2;
  return `${C + R * Math.cos(a)},${C + R * Math.sin(a)}`;
}

/**
 * Parts of a whole as a ring, with a legend listing each slice and its value
 * (zero slices included). Tones go by position, so put the slice that matters
 * first — "Used" before "Free". A slice marked `tone: 'danger'` is red instead
 * and leaves the others' tones as they were. `center` sits in the hole.
 */
export function DonutChart({
  label,
  slices,
  center,
  formatValue,
}: {
  /** What the chart shows, for assistive technology. */
  label: string;
  slices: { label: string; value: number; tone?: 'danger' }[];
  center?: ReactNode;
  formatValue: (value: number) => string;
}) {
  const total = slices.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  let position = 0;
  const tones = slices.map((s) =>
    s.tone === 'danger'
      ? 'donut-chart-tone-danger'
      : TONES[position++ % TONES.length],
  );
  let start = 0;

  return (
    <div role="figure" aria-label={label} className="donut-chart">
      <div className="donut-chart-ring">
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          aria-hidden="true"
        >
          <circle
            cx={C}
            cy={C}
            r={R}
            strokeWidth={THICKNESS}
            className="donut-chart-track"
          />
          {total > 0 &&
            slices.map((slice, i) => {
              const share = Math.max(0, slice.value) / total;
              const from = start;
              start += share;
              const tone = `donut-chart-slice ${tones[i]}`;
              if (share === 0) return null;
              if (share >= 1)
                return (
                  <circle
                    key={i}
                    cx={C}
                    cy={C}
                    r={R}
                    strokeWidth={THICKNESS}
                    className={tone}
                  />
                );
              return (
                <path
                  key={i}
                  d={`M${at(from)}A${R},${R} 0 ${share > 0.5 ? 1 : 0} 1 ${at(start)}`}
                  strokeWidth={THICKNESS}
                  className={tone}
                />
              );
            })}
        </svg>
        {center !== undefined && (
          <div className="donut-chart-center">{center}</div>
        )}
      </div>
      <ul className="donut-chart-legend">
        {slices.map((slice, i) => (
          <li key={i} className="donut-chart-legend-item">
            <span
              aria-hidden="true"
              className={`donut-chart-swatch ${tones[i]}`}
            />
            <span className="donut-chart-legend-label">{slice.label}</span>
            <span className="donut-chart-legend-value">
              {formatValue(slice.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
